import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import LanguageSelector from "@/components/LanguageSelector";
import {
  ArrowLeft,
  ShieldCheck,
  Sparkle,
  X,
  Check,
  WhatsappLogo,
  ChatCircleDots,
  Clock,
  Bank,
} from "@phosphor-icons/react";

function Pill({ children, tone = "indigo" }) {
  const tones = {
    indigo: "border-indigo-400/30 bg-indigo-500/[0.08] text-indigo-200",
    violet: "border-violet-400/30 bg-violet-500/[0.08] text-violet-200",
    emerald: "border-emerald-400/30 bg-emerald-500/[0.08] text-emerald-200",
    rose: "border-rose-400/30 bg-rose-500/[0.08] text-rose-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-[0.2em] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export default function TermsPage() {
  const { t, i18n } = useTranslation("terms");
  const positiveItems = t("whatIs.items", { returnObjects: true }) || [];
  const negativeItems = t("whatIsNot.items", { returnObjects: true }) || [];
  const philosophyItems = t("philosophy.items", { returnObjects: true }) || [];
  const sectionItems = t("sections.items", { returnObjects: true }) || [];

  const dateLocale =
    { en: "en-IN", hi: "hi-IN", te: "te-IN" }[i18n.resolvedLanguage] || "en-IN";

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <main
        data-testid="terms-root"
        className="flex-1 mx-auto max-w-4xl px-5 md:px-8 py-10 md:py-16 w-full"
      >
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="text-sm text-neutral-500 hover:text-neutral-200 inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>
          <LanguageSelector />
        </div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Pill tone="indigo">
              <ShieldCheck size={11} weight="fill" /> {t("hero.badgeLegal")}
            </Pill>
            <Pill tone="violet">
              <Sparkle size={11} weight="fill" /> {t("hero.badgeTrustTech")}
            </Pill>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
            {t("hero.titlePrefix")}
            <span className="text-violet-400">{t("hero.titleAccent")}</span>
            {t("hero.titleSuffix")}
          </h1>
          <p className="mt-6 text-neutral-400 text-base md:text-[17px] leading-relaxed max-w-2xl">
            {t("hero.body")}
          </p>
          <p className="mt-3 text-[13px] text-neutral-500">
            {t("hero.consent")}
          </p>
        </motion.div>

        {/* Positioning statement */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-12 lele-card p-8 md:p-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/[0.04] via-transparent to-indigo-500/[0.04] pointer-events-none" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.3em] text-violet-300 mb-3">
              {t("positioning.eyebrow")}
            </div>
            <p className="font-heading text-2xl md:text-3xl leading-snug text-neutral-100">
              {t("positioning.statement")}
            </p>
            <p className="mt-4 text-neutral-400 leading-relaxed max-w-2xl">
              {t("positioning.body")}
            </p>
          </div>
        </motion.div>

        {/* What LeLe is / isn't */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lele-card p-6 md:p-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="h-8 w-8 rounded-lg bg-emerald-500/10 border border-emerald-400/30 grid place-items-center">
                <Check size={16} weight="bold" className="text-emerald-300" />
              </span>
              <div className="font-heading text-lg">{t("whatIs.title")}</div>
            </div>
            <p className="text-[13px] text-neutral-500 mb-4">
              {t("whatIs.hint")}
            </p>
            <ul className="space-y-2">
              {positiveItems.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2.5 text-[15px] text-neutral-200"
                >
                  <Check
                    size={14}
                    weight="bold"
                    className="text-emerald-400 mt-1 shrink-0"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="lele-card p-6 md:p-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="h-8 w-8 rounded-lg bg-rose-500/10 border border-rose-400/30 grid place-items-center">
                <X size={16} weight="bold" className="text-rose-300" />
              </span>
              <div className="font-heading text-lg">{t("whatIsNot.title")}</div>
            </div>
            <p className="text-[13px] text-neutral-500 mb-4">
              {t("whatIsNot.hintPrefix")}
              <span className="text-neutral-200 font-medium">
                {t("whatIsNot.hintAccent")}
              </span>
              {t("whatIsNot.hintSuffix")}
            </p>
            <ul className="space-y-2">
              {negativeItems.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2.5 text-[15px] text-neutral-200"
                >
                  <X
                    size={14}
                    weight="bold"
                    className="text-rose-400 mt-1 shrink-0"
                  />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[13px] text-neutral-400 leading-relaxed">
              {t("whatIsNot.disclaimer")}
            </p>
          </motion.div>
        </div>

        {/* Core philosophy */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-14"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-violet-300 mb-3">
            {t("philosophy.eyebrow")}
          </div>
          <h2 className="font-heading text-2xl md:text-3xl mb-6">
            {t("philosophy.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {philosophyItems.map((p, idx) => (
              <div key={idx} className="lele-card p-6 md:p-7">
                <div className="text-violet-300 mb-2">
                  <Sparkle size={14} weight="fill" />
                </div>
                <div className="font-heading text-lg text-neutral-100 mb-2">
                  {p.title}
                </div>
                <p className="text-[14px] text-neutral-400 leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Numbered sections */}
        <div className="mt-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-300 mb-3">
            {t("sections.eyebrow")}
          </div>
          <h2 className="font-heading text-2xl md:text-3xl mb-8">
            {t("sections.title")}
          </h2>
          <div className="space-y-4">
            {sectionItems.map((s, idx) => (
              <motion.section
                key={idx}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (idx % 4) * 0.03 }}
                className="lele-card p-6 md:p-8"
              >
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="font-heading text-2xl text-violet-300 tabular-nums">
                    {s.n}
                  </span>
                  <h3 className="font-heading text-xl text-neutral-100 tracking-tight">
                    {s.title}
                  </h3>
                </div>
                <div className="pl-0 md:pl-12 space-y-3">
                  {(s.body || []).map((p, i) => (
                    <p
                      key={i}
                      className="text-neutral-400 text-[15px] leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>
        </div>

        {/* Support */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-16"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-300 mb-3">
            {t("support.eyebrow")}
          </div>
          <h2 className="font-heading text-2xl md:text-3xl mb-2">
            {t("support.title")}
          </h2>
          <p className="text-neutral-400 max-w-xl mb-6">
            {t("support.body")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="https://wa.me/917075521016"
              target="_blank"
              rel="noreferrer"
              className="lele-card p-6 flex flex-col justify-between hover:border-emerald-400/30 transition-colors group"
              data-testid="terms-whatsapp-link"
            >
              <div>
                <span className="inline-flex h-9 w-9 rounded-lg bg-emerald-500/10 border border-emerald-400/30 items-center justify-center mb-3">
                  <WhatsappLogo size={17} weight="fill" className="text-emerald-300" />
                </span>
                <div className="font-heading text-lg mb-1">
                  {t("support.whatsappTitle")}
                </div>
                <div className="text-[13px] text-neutral-500">
                  {t("support.whatsappHint")}
                </div>
              </div>
              <div className="mt-4 text-[13px] text-emerald-200 group-hover:text-emerald-100 inline-flex items-center gap-1.5">
                <WhatsappLogo size={13} weight="fill" />
                {t("support.whatsappOpen") || "Open WhatsApp"}
              </div>
            </a>

            <div className="lele-card p-6 flex flex-col justify-between">
              <div>
                <span className="inline-flex h-9 w-9 rounded-lg bg-indigo-500/10 border border-indigo-400/30 items-center justify-center mb-3">
                  <ChatCircleDots size={17} weight="fill" className="text-indigo-300" />
                </span>
                <div className="font-heading text-lg mb-1">
                  {t("support.chatTitle")}
                </div>
                <div className="text-[13px] text-neutral-500">
                  {t("support.chatHint")}
                </div>
              </div>
              <div className="mt-4 text-[13px] text-indigo-200">
                {t("support.chatFooter")}
              </div>
            </div>

            <div className="lele-card p-6 flex flex-col justify-between">
              <div>
                <span className="inline-flex h-9 w-9 rounded-lg bg-violet-500/10 border border-violet-400/30 items-center justify-center mb-3">
                  <Clock size={17} weight="fill" className="text-violet-300" />
                </span>
                <div className="font-heading text-lg mb-1">
                  {t("support.hoursTitle")}
                </div>
                <div className="text-[13px] text-neutral-500">
                  {t("support.hoursLine1")}
                  <br />
                  {t("support.hoursLine2")}
                </div>
              </div>
              <div className="mt-4 text-[13px] text-violet-200">
                {t("support.hoursFooter")}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Closing statement */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 lele-card p-8 md:p-12 relative overflow-hidden text-center"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/[0.08] via-indigo-500/[0.04] to-transparent pointer-events-none" />
          <div className="relative">
            <div className="mx-auto mb-4 inline-flex items-center gap-2">
              <Bank size={18} weight="duotone" className="text-violet-300" />
              <span className="font-heading text-xl md:text-2xl">
                {t("closing.brand")}
              </span>
            </div>
            <p className="font-heading text-2xl md:text-3xl leading-tight text-neutral-100">
              {t("closing.platform")}
            </p>
            <p className="mt-2 text-neutral-400 text-base">
              {t("closing.line1")}
            </p>
            <p className="mt-1 text-neutral-500 text-sm">
              {t("closing.line2")}
            </p>
            <div className="mt-6 text-[13px] uppercase tracking-[0.28em] text-violet-300">
              {t("closing.motto")}
            </div>
          </div>
        </motion.div>

        <div className="mt-10 text-[11px] uppercase tracking-[0.3em] text-neutral-600 text-center">
          {t("lastUpdated")} ·{" "}
          {new Date().toLocaleDateString(dateLocale, {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
